// Adventure finder: filters over the light quest-card DTO, shared by the
// homepage finder, the /quests catalog and the mood tiles.
//
// Why (2026-10-12): the site asked visitors to browse until something caught
// their eye. The finder answers "I have two hours, two kids and $30" with a
// shortlist. It runs on card metadata the server precomputes (time bucket,
// audience, honest cost), never on an AI call. Browser-safe: imports only the
// pure shared modules.

import type { QuestCard } from '@irl-sidequests/shared/quest-card';
import { timeBucketFor, type TimeBucket } from '@irl-sidequests/shared/editorial-standards';

export type FinderTime = 'quick' | 'short' | 'half-day' | 'full-day' | 'any';
export type FinderWho = 'solo' | 'date' | 'friends' | 'family' | 'any';
export type FinderBudget = 'free' | 'under25' | 'under100' | 'any';
export type FinderMood = 'challenge' | 'connect' | 'learn' | 'outside' | 'make' | 'any';

export interface FinderFilters {
  time: FinderTime;
  who: FinderWho;
  budget: FinderBudget;
  mood: FinderMood;
  category?: string;
  difficulty?: string;
  location?: string;
  search?: string;
}

export const EMPTY_FILTERS: FinderFilters = { time: 'any', who: 'any', budget: 'any', mood: 'any' };

export const TIME_OPTIONS: Array<{ value: Exclude<FinderTime, 'any'>; label: string; sub: string }> = [
  { value: 'quick', label: '15–30 min', sub: 'A quick one' },
  { value: 'short', label: 'About an hour', sub: 'After work' },
  { value: 'half-day', label: 'Half a day', sub: 'Saturday morning' },
  { value: 'full-day', label: 'All day', sub: 'Make a day of it' },
];

export const WHO_OPTIONS: Array<{ value: Exclude<FinderWho, 'any'>; label: string; emoji: string }> = [
  { value: 'solo', label: 'Just me', emoji: '🙂' },
  { value: 'date', label: 'Date night', emoji: '❤️' },
  { value: 'friends', label: 'Friends', emoji: '👯' },
  { value: 'family', label: 'Family', emoji: '👨‍👩‍👧' },
];

export const BUDGET_OPTIONS: Array<{ value: FinderBudget; label: string }> = [
  { value: 'free', label: 'Free' },
  { value: 'under25', label: 'Under $25' },
  { value: 'under100', label: 'Under $100' },
  { value: 'any', label: 'Any budget' },
];

/**
 * The emotional navigation layer. Where a page already owns the intent (the
 * friends hub, the category pages, the generator) the tile goes there; the
 * rest are catalog filters.
 */
export const MOODS: Array<{ value: FinderMood | 'surprise'; label: string; sub: string; emoji: string; href: string }> = [
  { value: 'challenge', label: 'Challenge yourself', sub: 'Test your skills', emoji: '🎯', href: '/quests?mood=challenge' },
  { value: 'connect', label: 'Connect with someone', sub: 'Make time together', emoji: '❤️', href: '/ideas/side-quests-with-friends' },
  { value: 'learn', label: 'Learn something new', sub: 'Feed your curiosity', emoji: '🔬', href: '/quests?mood=learn' },
  { value: 'outside', label: 'Get outside', sub: 'Explore the world', emoji: '🌲', href: '/category/nature-outdoors' },
  { value: 'make', label: 'Make something', sub: 'Get creative', emoji: '🎨', href: '/category/creative-arts' },
  { value: 'surprise', label: 'Surprise me', sub: 'Try something unexpected', emoji: '🎲', href: '/generator' },
];

const hay = (c: QuestCard) => `${c.title} ${c.hook} ${(c.tags || []).join(' ')}`.toLowerCase();
const CHALLENGE_RE = /challenge|compet|score|race|record|beat|master|accuracy|speed|marathon|test|versus|tournament|bullseye|timed|streak/i;
const LEARN_RE = /learn|skill|class|workshop|lesson|study|science|history|how to|basics|101|identify|decode|read |language|experiment/i;
const CONNECT_RE = /friend|date|famil|party|group|together|dinner|game night|conversation|host|neighbo|community|couple|partner/i;
const MAKE_RE = /make|build|craft|print|paint|sketch|draw|cook|bake|brew|carve|sew|design|pottery|candle|knit|photograph|write|compose/i;

export function cardTimeBucket(c: QuestCard): TimeBucket {
  return c.timeBucket || timeBucketFor(c.timeRequired);
}

export function timeMatches(c: QuestCard, t: FinderTime): boolean {
  if (t === 'any') return cardTimeBucket(c) !== 'ongoing';
  return cardTimeBucket(c) === t;
}

/**
 * Audience match. Rows the editorial upgrade has processed carry `bestFor`;
 * older rows only have the (92%-true) family flag, so they stay lenient to
 * avoid an empty finder while the catalog is upgraded.
 */
export function whoMatches(c: QuestCard, who: FinderWho): boolean {
  if (who === 'any') return true;
  if (c.bestFor && c.bestFor.length) {
    if (who === 'family') return (c.bestFor.includes('family') || c.bestFor.includes('kids')) && !c.adultsOnly;
    if (who === 'friends') return c.bestFor.includes('friends') || c.bestFor.includes('group');
    return c.bestFor.includes(who);
  }
  if (who === 'family') return c.familyFriendly !== false;
  if (who === 'solo') return c.category !== 'Social & Community';
  return true;
}

export function budgetMatches(c: QuestCard, b: FinderBudget): boolean {
  if (b === 'any') return true;
  const gear = c.gearFrom ?? 0;
  const kind = c.costKind || (c.costTier === 'Free' ? 'free' : 'free-if-equipped');
  if (b === 'free') return kind === 'free' && gear === 0;
  if (kind === 'paid') return b === 'under100';
  if (b === 'under25') return gear <= 25;
  return gear <= 100;
}

export function moodMatches(c: QuestCard, mood: FinderMood): boolean {
  if (mood === 'any') return true;
  const h = hay(c);
  switch (mood) {
    case 'challenge': return c.difficulty !== 'Beginner' || CHALLENGE_RE.test(h);
    case 'learn': return c.category === 'Personal Growth' || LEARN_RE.test(h);
    case 'connect': return c.category === 'Social & Community' || CONNECT_RE.test(h) || !!c.bestFor?.some(g => g === 'friends' || g === 'date' || g === 'group');
    case 'outside': return c.location === 'Outdoor' || c.category === 'Nature & Outdoors';
    case 'make': return c.category === 'Creative Arts' || MAKE_RE.test(h);
    default: return true;
  }
}

export function searchMatches(c: QuestCard, q: string | undefined): boolean {
  if (!q) return true;
  const s = q.toLowerCase();
  return c.title.toLowerCase().includes(s) || c.description.toLowerCase().includes(s) || c.hook.toLowerCase().includes(s)
    || (c.tags || []).some(t => t.toLowerCase().includes(s)) || c.category.toLowerCase().includes(s);
}

export function matchesFinder(c: QuestCard, f: FinderFilters): boolean {
  return timeMatches(c, f.time)
    && whoMatches(c, f.who)
    && budgetMatches(c, f.budget)
    && moodMatches(c, f.mood)
    && (!f.category || c.category === f.category)
    && (!f.difficulty || c.difficulty === f.difficulty)
    && (!f.location || c.location === f.location)
    && searchMatches(c, f.search);
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

/** Upgraded rows first (they carry a mission and honest cost), then the homepage set, then a stable shuffle. */
export function rankForFinder(cards: QuestCard[], seed = 0): QuestCard[] {
  return [...cards].sort((a, b) => {
    const tier = (c: QuestCard) => ((c.contentVersion ?? 1) >= 2 ? 0 : 1) * 10 + (c.featured ? 0 : c.trending ? 1 : 2);
    const d = tier(a) - tier(b);
    if (d) return d;
    return (hash(a.slug + seed) % 1000) - (hash(b.slug + seed) % 1000);
  });
}

/** Interleave categories so a grid never shows five of one kind in a row. */
export function interleaveByCategory(cards: QuestCard[]): QuestCard[] {
  const buckets = new Map<string, QuestCard[]>();
  for (const c of cards) {
    if (!buckets.has(c.category)) buckets.set(c.category, []);
    buckets.get(c.category)!.push(c);
  }
  const out: QuestCard[] = [];
  const lists = [...buckets.values()];
  for (let i = 0; out.length < cards.length; i++) {
    for (const l of lists) if (l[i]) out.push(l[i]);
  }
  return out;
}

/** Progressive relaxation when a strict combination finds nothing: drop budget, then company, then time. */
export function findWithFallback(cards: QuestCard[], f: FinderFilters, min = 3): { results: QuestCard[]; relaxed: Array<keyof FinderFilters> } {
  const relaxed: Array<keyof FinderFilters> = [];
  let filters = { ...f };
  let results = cards.filter(c => matchesFinder(c, filters));
  for (const key of ['budget', 'who', 'time'] as const) {
    if (results.length >= min) break;
    if (filters[key] === 'any') continue;
    filters = { ...filters, [key]: 'any' };
    relaxed.push(key);
    results = cards.filter(c => matchesFinder(c, filters));
  }
  return { results, relaxed };
}

const TIMES: readonly string[] = ['quick', 'short', 'half-day', 'full-day'];
const WHOS: readonly string[] = ['solo', 'date', 'friends', 'family'];
const BUDGETS: readonly string[] = ['free', 'under25', 'under100'];
const MOOD_VALUES: readonly string[] = ['challenge', 'connect', 'learn', 'outside', 'make'];

export function parseFinderParams(sp: URLSearchParams | Record<string, string | string[] | undefined>): FinderFilters {
  const get = (k: string): string | undefined => {
    if (sp instanceof URLSearchParams) return sp.get(k) ?? undefined;
    const v = sp[k];
    return Array.isArray(v) ? v[0] : v;
  };
  const pick = <T extends string>(k: string, allowed: readonly string[], fallback: T): T => {
    const v = get(k);
    return v && allowed.includes(v) ? (v as T) : fallback;
  };
  return {
    time: pick<FinderTime>('t', TIMES, 'any'),
    who: pick<FinderWho>('w', WHOS, 'any'),
    budget: pick<FinderBudget>('b', BUDGETS, 'any'),
    mood: pick<FinderMood>('mood', MOOD_VALUES, 'any'),
    category: get('category') || undefined,
    difficulty: get('difficulty') || undefined,
    location: get('location') || undefined,
    search: get('search')?.trim() || undefined,
  };
}

export function finderParams(f: FinderFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.time !== 'any') p.set('t', f.time);
  if (f.who !== 'any') p.set('w', f.who);
  if (f.budget !== 'any') p.set('b', f.budget);
  if (f.mood !== 'any') p.set('mood', f.mood);
  if (f.category) p.set('category', f.category);
  if (f.difficulty) p.set('difficulty', f.difficulty);
  if (f.location) p.set('location', f.location);
  if (f.search) p.set('search', f.search);
  return p;
}

export function hasAnyFilter(f: FinderFilters): boolean {
  return finderParams(f).toString().length > 0;
}
